"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MainScreen = MainScreen;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
var react_redux_1 = require("react-redux");
var react_router_dom_1 = require("react-router-dom");
var content_1 = require("../components/dialogs/content");
var message_1 = require("../components/dialogs/message");
var admin_layout_1 = require("../components/layouts/admin-layout");
var site_layout_1 = require("../components/layouts/site-layout");
var site_renderer_1 = require("../components/site-renderer");
var sites_1 = require("../features/apis/sites");
var site_1 = require("../features/slices/site");
var useSiteConfig_1 = require("../hooks/useSiteConfig");
var useSiteConfigBySlug_1 = require("../hooks/useSiteConfigBySlug");
var domain_routing_1 = require("../utils/domain-routing");
var admin_dashboard_1 = require("./admin/admin-dashboard");
// Route wrapper for site display using useParams
// Supports both ID-based routing (/site/689878177c8f79da1bcb3beb) and slug-based routing (/site/janeel)
var SiteRoute = function () {
    var siteId = (0, react_router_dom_1.useParams)().siteId;
    // If no siteId provided, show a message
    if (!siteId) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "min-h-screen flex items-center justify-center", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)("h1", { className: "text-2xl font-bold text-gray-900 mb-4", children: "No Site Selected" }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600 mb-4", children: "Please select a site to view." }), (0, jsx_runtime_1.jsx)("a", { href: "/admin", className: "px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700", children: "Go to Admin Dashboard" })] }) }));
    }
    // Detect if siteId is a MongoDB ObjectID (24 hex characters) or a slug
    var isMongoId = /^[0-9a-fA-F]{24}$/.test(siteId);
    // Use appropriate hook based on parameter type
    var _a = isMongoId
        ? (0, useSiteConfig_1.useSiteConfig)(siteId)
        : (0, useSiteConfigBySlug_1.useSiteConfigBySlug)(siteId), config = _a.config, loading = _a.loading, error = _a.error;
    if (loading) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "min-h-screen flex items-center justify-center", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)("div", { className: "animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto" }), (0, jsx_runtime_1.jsx)("p", { className: "mt-4 text-gray-600", children: "Loading site..." })] }) }));
    }
    if (error || !config) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "min-h-screen flex items-center justify-center", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)("h1", { className: "text-2xl font-bold text-red-600 mb-4", children: "Site Not Found" }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600 mb-4", children: error || "Site \"".concat(siteId, "\" could not be loaded.") }), (0, jsx_runtime_1.jsx)("a", { href: "/admin", className: "px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700", children: "Go to Admin Dashboard" })] }) }));
    }
    return (0, jsx_runtime_1.jsx)(site_renderer_1.SiteRenderer, { config: config });
};
var renderLoadingState = function (message) { return ((0, jsx_runtime_1.jsx)("div", { className: "min-h-screen flex items-center justify-center", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)("div", { className: "animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto" }), (0, jsx_runtime_1.jsx)("p", { className: "mt-4 text-gray-600", children: message })] }) })); };
// Redirect root to the configured default site (by slug if present, otherwise by id)
var DefaultSiteRedirect = function () {
    var _a;
    var dispatch = (0, react_redux_1.useDispatch)();
    var hostname = typeof window !== "undefined" ? window.location.hostname : "";
    var normalizedHost = hostname.toLowerCase();
    var isPrimaryHost = !hostname ||
        normalizedHost === "localhost" ||
        normalizedHost === "127.0.0.1" ||
        normalizedHost.includes("lewfin-providers-web-dev") ||
        normalizedHost.includes("lewfin-providers-web-prod");
    var shouldQuerySites = Boolean(hostname) && !isPrimaryHost;
    var _b = (0, sites_1.useGetAllSitesQuery)(undefined, {
        skip: !shouldQuerySites,
    }), allSitesData = _b.data, isSitesFetching = _b.isFetching, allSitesError = _b.error;
    var _c = (0, react_1.useMemo)(function () {
        var _a;
        if (!(allSitesData === null || allSitesData === void 0 ? void 0 : allSitesData.success) || !((_a = allSitesData.content) === null || _a === void 0 ? void 0 : _a.sites)) {
            return { matchedSite: null, matchedSiteId: null };
        }
        var found = (0, domain_routing_1.findSiteForHost)(hostname, allSitesData.content.sites);
        if (!found)
            return { matchedSite: null, matchedSiteId: null };
        var id = (found.slug && found.slug.trim().length > 0
            ? found.slug.trim()
            : found._id) || null;
        return { matchedSite: found, matchedSiteId: id };
    }, [hostname, allSitesData]), matchedSite = _c.matchedSite, matchedSiteId = _c.matchedSiteId;
    // Custom-domain root traffic renders SiteRenderer directly without going
    // through SiteRoute, so the useSiteConfig/useSiteConfigBySlug hooks that
    // normally populate site.current never run. Mirror their dispatch here so
    // FaceTec branding (which reads state.site.current) gets the tenant config
    // instead of falling back to Dupwies/Lewfin defaults.
    (0, react_1.useEffect)(function () {
        if (matchedSite) {
            dispatch((0, site_1.setCurrentSite)(matchedSite));
        }
    }, [matchedSite, dispatch]);
    var waitingForDomainResolution = shouldQuerySites && !matchedSiteId && isSitesFetching && !allSitesError;
    var _d = (0, sites_1.useGetDefaultSiteQuery)(undefined, {
        skip: Boolean(matchedSiteId),
    }), data = _d.data, isLoading = _d.isLoading, error = _d.error;
    if (matchedSite) {
        // Render matched site directly at root so URL stays the same
        return (0, jsx_runtime_1.jsx)(site_renderer_1.SiteRenderer, { config: matchedSite });
    }
    if (matchedSiteId) {
        return (0, jsx_runtime_1.jsx)(react_router_dom_1.Navigate, { to: "/site/".concat(matchedSiteId), replace: true });
    }
    if (shouldQuerySites && !isSitesFetching && !matchedSiteId) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "min-h-screen flex items-center justify-center", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)("h1", { className: "text-2xl font-bold text-red-600 mb-4", children: "Site Not Configured" }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600 mb-4", children: "No site is assigned to this domain. Please verify your domain setup or contact support." })] }) }));
    }
    if (waitingForDomainResolution || isLoading) {
        return renderLoadingState("Loading site...");
    }
    if (!(data === null || data === void 0 ? void 0 : data.success) || !((_a = data.content) === null || _a === void 0 ? void 0 : _a.site) || error) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "min-h-screen flex items-center justify-center", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)("h1", { className: "text-2xl font-bold text-red-600 mb-4", children: "Default Site Not Configured" }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600 mb-4", children: "Please set a default site in the admin panel." }), (0, jsx_runtime_1.jsx)("a", { href: "/admin", className: "px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700", children: "Go to Admin Dashboard" })] }) }));
    }
    var defaultSite = data.content.site;
    var target = defaultSite.slug && defaultSite.slug.trim().length > 0
        ? "/site/".concat(defaultSite.slug)
        : "/site/".concat(defaultSite._id);
    return (0, jsx_runtime_1.jsx)(react_router_dom_1.Navigate, { to: target, replace: true });
};
// Legacy test component
var TestScreen = function () {
    return ((0, jsx_runtime_1.jsxs)("div", { className: "", children: [(0, jsx_runtime_1.jsx)("h1", { children: "Test Screen - Legacy Component" }), (0, jsx_runtime_1.jsx)("p", { children: "This is a test screen for development purposes." })] }));
};
function MainScreen() {
    var routes = (0, react_router_dom_1.useRoutes)([
        {
            path: "/",
            element: (0, jsx_runtime_1.jsx)(DefaultSiteRedirect, {}),
        },
        {
            path: "/admin",
            element: (0, jsx_runtime_1.jsx)(admin_layout_1.AdminLayout, {}),
            children: [
                {
                    index: true,
                    element: (0, jsx_runtime_1.jsx)(admin_dashboard_1.AdminDashboard, {}),
                },
                {
                    path: "sites",
                    element: (0, jsx_runtime_1.jsx)("div", { children: "Sites Management - Coming Soon" }),
                },
                {
                    path: "settings",
                    element: (0, jsx_runtime_1.jsx)("div", { children: "Admin Settings - Coming Soon" }),
                },
            ],
        },
        {
            path: "/site",
            element: (0, jsx_runtime_1.jsx)(site_layout_1.SiteLayout, {}),
            children: [
                {
                    path: ":siteId",
                    element: (0, jsx_runtime_1.jsx)(SiteRoute, {}),
                },
            ],
        },
        {
            path: "/preview",
            element: (0, jsx_runtime_1.jsx)(site_layout_1.SiteLayout, {}),
            children: [
                {
                    index: true,
                    element: ((0, jsx_runtime_1.jsx)("div", { className: "min-h-screen flex items-center justify-center", children: (0, jsx_runtime_1.jsxs)("div", { className: "text-center", children: [(0, jsx_runtime_1.jsx)("h1", { className: "text-2xl font-bold text-gray-900 mb-4", children: "Preview Mode" }), (0, jsx_runtime_1.jsx)("p", { className: "text-gray-600 mb-4", children: "Please select a site to preview." }), (0, jsx_runtime_1.jsx)("a", { href: "/admin", className: "px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700", children: "Go to Admin Dashboard" })] }) })),
                },
            ],
        },
        {
            path: "/test",
            element: (0, jsx_runtime_1.jsx)(TestScreen, {}),
        },
        {
            path: "*",
            element: (0, jsx_runtime_1.jsx)(react_router_dom_1.Navigate, { to: "/", replace: true }),
        },
    ]);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [routes, (0, jsx_runtime_1.jsx)(content_1.ContentDialog, {}), (0, jsx_runtime_1.jsx)(message_1.ModalMessage, {})] }));
}
